/** Supabase reads. RLS scopes every query to what the session may see. */
import type {
  InterventionRow,
  MediaRow,
  ProjectRow,
  SpeciesRow,
  SurveyRow,
  SurveySummaryRow,
  TeamRow,
  TreeFlagsRow,
  TreeLatestRow,
  TreeRow,
  ZoneRow,
  ZoneSurvivalRow,
} from '@treetrack/protocol';
import type { MeasurementWithGirths } from './aggregate';
import { demoData, isDemo } from './demo';
import { fetchAll, supabase, unwrap } from './supabase';

export interface ProjectSummary {
  project_id: string;
  code: string;
  name: string;
  tree_count: number;
  measured_now: number;
  alive_now: number;
  survival_pct: number | null;
  last_survey_date: string | null;
  zone_count: number;
}

export interface ProjectBundle {
  project: ProjectRow;
  zones: ZoneRow[];
  species: SpeciesRow[];
  trees: TreeRow[];
  latest: Map<string, TreeLatestRow>;
  latestRows: TreeLatestRow[];
  surveys: SurveyRow[];
  summaries: SurveySummaryRow[];
  zoneSurvival: ZoneSurvivalRow[];
  measurements: MeasurementWithGirths[];
  interventions: InterventionRow[];
  flags: TreeFlagsRow[];
  media: MediaRow[];
  loadedAt: string;
}

function db() {
  if (!supabase) throw new Error('backend not configured');
  return supabase;
}

function demoProjects(): ProjectSummary[] {
  const d = demoData();
  const measured = d.latest.filter((l) => l.condition === 'H' || l.condition === 'S' || l.condition === 'D').length;
  const alive = d.latest.filter((l) => l.condition === 'H' || l.condition === 'S').length;
  return [
    {
      project_id: d.project.id,
      code: d.project.code,
      name: d.project.name,
      tree_count: d.trees.length,
      measured_now: measured,
      alive_now: alive,
      survival_pct: measured ? Math.round((1000 * alive) / measured) / 10 : null,
      last_survey_date: d.surveys.map((s) => s.date).sort().pop() ?? null,
      zone_count: d.zones.length,
    },
  ];
}

function demoBundle(): ProjectBundle {
  const d = demoData();
  const girthsByM = new Map<string, typeof d.girths>();
  for (const g of d.girths) girthsByM.set(g.measurement_id, [...(girthsByM.get(g.measurement_id) ?? []), g]);
  return {
    project: d.project,
    zones: d.zones,
    species: d.species,
    trees: d.trees,
    latest: new Map(d.latest.map((l) => [l.tree_id, l])),
    latestRows: d.latest,
    surveys: [...d.surveys].sort((a, b) => (a.date < b.date ? 1 : -1)),
    summaries: d.summaries,
    zoneSurvival: d.zoneSurvival,
    measurements: d.measurements.map((m) => ({ ...m, measurement_girths: girthsByM.get(m.id) ?? [] })),
    interventions: d.interventions,
    flags: d.flags.filter((f) => f.stressed_twice || f.not_found_last || f.not_found_twice || f.possible_mis_tag),
    media: [],
    loadedAt: new Date().toISOString(),
  };
}

export async function loadProjects(): Promise<ProjectSummary[]> {
  if (isDemo()) return demoProjects();
  return unwrap(await db().from('project_summary').select('*').order('name')) as ProjectSummary[];
}

export async function loadSpecies(): Promise<SpeciesRow[]> {
  if (isDemo()) return demoData().species;
  return unwrap(await db().from('species').select('*').order('name_es')) as SpeciesRow[];
}

export async function loadBundle(projectId: string): Promise<ProjectBundle> {
  if (isDemo()) {
    if (projectId !== demoData().project.id) throw new Error('demo: unknown project');
    return demoBundle();
  }
  const s = db();
  const [project, zones, species, trees, latestRows, surveys, summaries, zoneSurvival, measurements, interventions, flags, media] = await Promise.all([
    unwrap(await s.from('projects').select('*').eq('id', projectId).single()) as Promise<ProjectRow> | ProjectRow,
    fetchAll<ZoneRow>((a, b) => s.from('zones').select('*').eq('project_id', projectId).order('label').range(a, b)),
    loadSpecies(),
    fetchAll<TreeRow>((a, b) => s.from('trees').select('*').eq('project_id', projectId).order('tag').range(a, b)),
    fetchAll<TreeLatestRow>((a, b) => s.from('tree_latest').select('*').eq('project_id', projectId).range(a, b)),
    fetchAll<SurveyRow>((a, b) => s.from('surveys').select('*').eq('project_id', projectId).order('date', { ascending: false }).range(a, b)),
    fetchAll<SurveySummaryRow>((a, b) => s.from('survey_summary').select('*').eq('project_id', projectId).range(a, b)),
    fetchAll<ZoneSurvivalRow>((a, b) => s.from('zone_survival_by_survey').select('*').eq('project_id', projectId).range(a, b)),
    fetchAll<MeasurementWithGirths & { surveys?: unknown }>((a, b) =>
      s.from('measurements').select('*, measurement_girths(*), surveys!inner(project_id)').eq('surveys.project_id', projectId).order('recorded_at').range(a, b),
    ),
    fetchAll<InterventionRow>((a, b) => s.from('interventions').select('*').eq('project_id', projectId).order('done_at', { ascending: false }).range(a, b)),
    fetchAll<TreeFlagsRow>((a, b) =>
      s.from('tree_flags').select('*').or('stressed_twice.is.true,not_found_last.is.true,not_found_twice.is.true,possible_mis_tag.is.true').range(a, b),
    ),
    fetchAll<MediaRow>((a, b) => s.from('media').select('*').eq('project_id', projectId).eq('upload_complete', true).order('taken_at').range(a, b)),
  ]);
  for (const m of measurements) delete (m as { surveys?: unknown }).surveys;
  const latest = new Map((latestRows as TreeLatestRow[]).map((l) => [l.tree_id, l]));
  return {
    project: project as ProjectRow,
    zones,
    species,
    trees,
    latest,
    latestRows: latestRows as TreeLatestRow[],
    surveys,
    summaries,
    zoneSurvival,
    measurements,
    interventions,
    flags,
    media,
    loadedAt: new Date().toISOString(),
  };
}

export async function loadTeams(projectId: string): Promise<TeamRow[]> {
  if (isDemo()) return [];
  return unwrap(await db().from('teams').select('id, project_id, name, active, created_at').eq('project_id', projectId).order('name')) as TeamRow[];
}

export interface MemberWithName {
  project_id: string;
  user_id: string;
  role: string;
  display_name: string;
}

export async function loadMembers(projectId: string): Promise<MemberWithName[]> {
  if (isDemo()) return [];
  const s = db();
  const members = unwrap(await s.from('project_members').select('*').eq('project_id', projectId)) as Array<{ project_id: string; user_id: string; role: string }>;
  if (members.length === 0) return [];
  const profiles = unwrap(await s.from('profiles').select('user_id, display_name').in('user_id', members.map((m) => m.user_id))) as Array<{ user_id: string; display_name: string }>;
  const names = new Map(profiles.map((p) => [p.user_id, p.display_name]));
  return members.map((m) => ({ ...m, display_name: names.get(m.user_id) ?? m.user_id.slice(0, 8) }));
}

export async function signedMediaUrl(path: string): Promise<string | null> {
  if (isDemo()) return null;
  const res = await db().storage.from('media').createSignedUrl(path, 3600);
  return res.data?.signedUrl ?? null;
}

export async function signedBasemapUrl(path: string): Promise<string | null> {
  if (isDemo()) return null;
  const res = await db().storage.from('basemap').createSignedUrl(path, 3600);
  return res.data?.signedUrl ?? null;
}
